/**
 * Captures UTM parameters and referrer on page load, persists in sessionStorage.
 * All form submissions should call getTrackingData() to attach tracking info.
 */

const STORAGE_KEY = "st_tracking";

interface TrackingData {
  lead_source: string;
  lead_medium: string;
  referrer_url: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
  /** Meta/Facebook ad attribution */
  fbclid: string;
  utm_campaign_id: string;
  utm_adset_id: string;
  utm_ad_id: string;
}

/** Read a browser cookie value (used for Meta's _fbp / _fbc cookies). */
function readCookie(name: string): string {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : "";
  } catch {
    return "";
  }
}

/**
 * Facebook click/browser identifiers, read live at submission time so the
 * freshest Meta Pixel cookies are always used.
 */
export function getMetaIdentifiers(): { fbp: string; fbc: string; fbclid: string } {
  const stored = getTrackingData();
  const fbclid = stored.fbclid || "";
  let fbc = readCookie("_fbc");
  if (!fbc && fbclid) fbc = `fb.1.${Date.now()}.${fbclid}`;
  return { fbp: readCookie("_fbp"), fbc, fbclid };
}

function categorizeSource(referrer: string, utmSource: string): string {
  const src = utmSource?.toLowerCase() || "";
  const ref = referrer?.toLowerCase() || "";

  if (src === "google" || ref.includes("google.com")) return "Google";
  if (src === "facebook" || ref.includes("facebook.com") || ref.includes("fb.com")) return "Facebook";
  if (src === "instagram" || ref.includes("instagram.com")) return "Instagram";
  if (src === "whatsapp" || ref.includes("whatsapp.com") || ref.includes("wa.me")) return "WhatsApp";
  if (src === "email" || src === "newsletter") return "Email";
  if (src === "tiktok" || ref.includes("tiktok.com")) return "TikTok";
  if (src) return src.charAt(0).toUpperCase() + src.slice(1);
  if (ref && !ref.includes(window.location.hostname)) return "Referral";
  return "Direct";
}

function captureTracking(): TrackingData {
  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer || "";
  const utmSource = params.get("utm_source") || "";
  const utmMedium = params.get("utm_medium") || "";
  const utmCampaign = params.get("utm_campaign") || "";
  const utmTerm = params.get("utm_term") || "";
  const utmContent = params.get("utm_content") || "";
  const fbclid = params.get("fbclid") || "";

  const leadSource = categorizeSource(referrer, utmSource) === "Direct" && fbclid
    ? "Facebook"
    : categorizeSource(referrer, utmSource);
  const leadMedium = utmMedium || (fbclid ? "paid-social" : referrer ? "referral" : "none");

  return {
    lead_source: leadSource,
    lead_medium: leadMedium,
    referrer_url: referrer,
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    utm_term: utmTerm,
    utm_content: utmContent,
    fbclid,
    utm_campaign_id: params.get("campaign_id") || params.get("utm_campaign_id") || "",
    utm_adset_id: params.get("adset_id") || params.get("utm_adset_id") || "",
    utm_ad_id: params.get("ad_id") || params.get("utm_ad_id") || "",
  };
}

/** Call once on app load to capture and persist tracking data */
export function initTracking(): void {
  const fresh = captureTracking();
  const existing = sessionStorage.getItem(STORAGE_KEY);
  if (existing) {
    // Only overwrite when this page view carries new ad attribution, so the
    // original source survives navigation around the site.
    const hasNewAttribution = Boolean(fresh.fbclid || fresh.utm_source || fresh.utm_ad_id);
    if (!hasNewAttribution) return;
  }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
}

/** Get stored tracking data to attach to form submissions */
export function getTrackingData(): Partial<TrackingData> {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return captureTracking();
}
